// contexts/ToolRegistryContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';

export interface Tool {
  id: string;
  country: 'uk' | 'canada' | 'australia' | 'germany' | 'new_zealand';
  name: string;
  description: string;
  path: string; // relative route path
  icon?: string; // icon name or component
  category?: string;
  requiresClient: boolean; // does the tool need a client context?
}

interface ToolRegistryContextType {
  tools: Tool[];
  getToolsByCountry: (country: Tool['country']) => Tool[];
  getToolById: (id: string) => Tool | undefined;
}

const ToolRegistryContext = createContext<ToolRegistryContextType | undefined>(undefined);

// Pre‑register all tools from your existing ecosystems.
// You can expand this list later.
const DEFAULT_TOOLS: Tool[] = [
  // UK
  { id: 'uk-flight-deck', country: 'uk', name: 'UK Flight Deck', description: 'Main UK immigration dashboard', path: '/uk-flight-deck', requiresClient: false },
  { id: 'uk-tools-ecosystem', country: 'uk', name: 'UK Tools Ecosystem', description: 'All UK premium tools', path: '/uk-tools-ecosystem', requiresClient: false },
  { id: 'uk-skilled-worker', country: 'uk', name: 'Skilled Worker Assessment', description: 'Assess skilled worker visa eligibility', path: '/skilled-worker-assessment', requiresClient: true },
  { id: 'uk-student', country: 'uk', name: 'Student Visa Assessment', description: 'Student visa eligibility', path: '/student-assessment', requiresClient: true },
  { id: 'uk-family', country: 'uk', name: 'Family Sponsorship', description: 'Family visa eligibility', path: '/family-assessment', requiresClient: true },
  // Canada
  { id: 'ca-flight-deck', country: 'canada', name: 'Canada Flight Deck', description: 'Main Canada immigration dashboard', path: '/canada-immigration-flight-deck', requiresClient: false },
  { id: 'ca-tools-ecosystem', country: 'canada', name: 'Canada Tools Ecosystem', description: 'All Canada premium tools', path: '/canada-tools-ecosystem', requiresClient: false },
  { id: 'ca-express-entry', country: 'canada', name: 'Express Entry CRS', description: 'Calculate CRS score', path: '/canada/express-entry-crs', requiresClient: true },
  { id: 'ca-pnp', country: 'canada', name: 'PNP Finder', description: 'Find best provincial nominee program', path: '/canada-pnp-finder', requiresClient: true },
  // Australia
  { id: 'au-flight-deck', country: 'australia', name: 'Australia Journey Compass', description: 'Main Australia immigration dashboard — Flight Deck retired, now UPRM-wired', path: '/journey-compass', requiresClient: false },
  { id: 'au-tools-ecosystem', country: 'australia', name: 'Australia Tools Ecosystem', description: 'All Australia premium tools', path: '/australia-tools-ecosystem', requiresClient: false },
  { id: 'au-skilled-independent', country: 'australia', name: 'Skilled Independent (189)', description: 'Points test for 189 visa', path: '/australia/skilled-independent', requiresClient: true },
  // Germany
  { id: 'de-flight-deck', country: 'germany', name: 'Germany Journey Compass', description: 'Main Germany immigration dashboard — Flight Deck retired, now UPRM-wired', path: '/journey-compass', requiresClient: false },
  { id: 'de-tools-ecosystem', country: 'germany', name: 'Germany Tools Ecosystem', description: 'All Germany premium tools', path: '/de-tools-ecosystem', requiresClient: false },
  { id: 'de-skilled-companion', country: 'germany', name: 'Germany Skilled Companion', description: 'Rule-based skilled / Blue Card readiness vault', path: '/germany-skilled-companion', requiresClient: true },
  { id: 'ie-skills-companion', country: 'ireland', name: 'Ireland Skills Companion', description: 'Rule-based CSEP/GEP/Student readiness vault', path: '/ireland-skills-companion', requiresClient: true },
  { id: 'nz-skilled-companion', country: 'newzealand', name: 'NZ Skilled Companion', description: 'Rule-based SMC/Green List/AEWV readiness vault', path: '/nz-skilled-companion', requiresClient: true },
  { id: 'de-eu-blue-card', country: 'germany', name: 'EU Blue Card', description: 'Check EU Blue Card eligibility', path: '/germany/eu-blue-card', requiresClient: true },
  // New Zealand
  { id: 'nz-flight-deck', country: 'new_zealand', name: 'NZ Flight Deck', description: 'Main NZ immigration dashboard', path: '/new-zealand-immigration-flight-deck', requiresClient: false },
  { id: 'nz-tools-ecosystem', country: 'new_zealand', name: 'NZ Tools Ecosystem', description: 'All NZ premium tools', path: '/nz-tools-ecosystem', requiresClient: false },
  { id: 'nz-smc', country: 'new_zealand', name: 'Skilled Migrant Category', description: 'Points assessment for SMC', path: '/nz-skilled-migrant-assessment', requiresClient: true },
];

export const ToolRegistryProvider: React.FC<{ children: ReactNode; customTools?: Tool[] }> = ({ children, customTools = [] }) => {
  const tools = [...DEFAULT_TOOLS, ...customTools];

  const getToolsByCountry = (country: Tool['country']) => {
    return tools.filter(t => t.country === country);
  };

  const getToolById = (id: string) => {
    return tools.find(t => t.id === id);
  };

  return (
    <ToolRegistryContext.Provider value={{ tools, getToolsByCountry, getToolById }}>
      {children}
    </ToolRegistryContext.Provider>
  );
};

export const useToolRegistry = () => {
  const context = useContext(ToolRegistryContext);
  if (context === undefined) {
    throw new Error('useToolRegistry must be used within a ToolRegistryProvider');
  }
  return context;
};